import gsap from 'gsap'

export function initQuoteLock(): void {
  const quoteSection = document.getElementById('quote')
  if (!quoteSection) return

  const quoteHeading = quoteSection.querySelector('.quote__heading') as HTMLElement | null
  const imageWrap = quoteSection.querySelector('.quote__hero-image-wrap') as HTMLElement | null
  const inlineSlot = quoteSection.querySelector('.quote__inline-slot') as HTMLElement | null
  const pulledToken = quoteSection.querySelector('#quote-token-pulled') as HTMLElement | null
  const byToken = quoteSection.querySelector('#quote-token-by') as HTMLElement | null
  const youRing = quoteSection.querySelector('.quote__you-ring') as SVGSVGElement | null

  if (!quoteHeading || !imageWrap || !inlineSlot || !pulledToken || !byToken) return

  const getDockTransform = () => {
    const imageRect = imageWrap.getBoundingClientRect()
    const slotRect = inlineSlot.getBoundingClientRect()
    const imageCenterX = imageRect.left + imageRect.width * 0.5
    const imageCenterY = imageRect.top + imageRect.height * 0.5
    const slotCenterX = slotRect.left + slotRect.width * 0.5
    const slotCenterY = slotRect.top + slotRect.height * 0.5

    const deltaX = slotCenterX - imageCenterX
    const deltaY = slotCenterY - imageCenterY - 15
    const scaleFactor = Math.min(
      slotRect.width / Math.max(imageRect.width, 1),
      slotRect.height / Math.max(imageRect.height, 1)
    )

    return {
      x: deltaX,
      y: deltaY,
      scale: scaleFactor,
    }
  }

  gsap.set(quoteHeading, { autoAlpha: 1, y: 0, scale: 1 })
  gsap.set(imageWrap, {
    autoAlpha: 1,
    y: 0,
    scale: 1,
    rotate: 0,
    transformOrigin: '50% 50%',
  })
  gsap.set(pulledToken, { x: -42 })
  gsap.set(byToken, { x: 42 })

  const scribbleVariants = [
    {
      viewBox: '0 0 310 40',
      d: 'M5 20.9998C65.6267 17.4649 126.268 13.845 187.208 12.8887C226.483 12.2723 265.751 13.2796 304.998 13.9998',
    },
    {
      viewBox: '0 0 310 40',
      d: 'M5 29.8857C52.3147 26.9322 99.4329 21.6611 146.503 17.1765C151.753 16.6763 157.115 15.9505 162.415 15.6551C163.28 15.6069 165.074 15.4123 164.383 16.4275C161.704 20.3627 157.134 23.7551 153.95 27.4983C153.209 28.3702 148.194 33.4751 150.669 34.6605C153.638 36.0819 163.621 32.6063 165.039 32.2029C178.55 28.3608 191.49 23.5968 204.869 19.5404C231.903 11.3436 259.347 5.83254 288.793 5.12258C294.094 4.99476 299.722 4.82265 305 5.45025',
    },
    {
      viewBox: '0 0 310 40',
      d: 'M17.0039 32.6826C32.2307 32.8412 47.4552 32.8277 62.676 32.8118C67.3044 32.807 96.546 33.0555 104.728 32.0775C113.615 31.0152 104.516 28.3028 102.022 27.2826C89.9573 22.3465 77.3751 19.0254 65.0451 15.0552C57.8987 12.7542 37.2813 8.49399 44.2314 6.10216C50.9667 3.78422 64.2873 5.81914 70.4249 5.96641C105.866 6.81677 141.306 7.58809 176.75 8.59886C217.874 9.77162 258.906 11.0553 300 14.4892',
    },
  ]

  let ringFinalScale = 1
  let youRingPath: SVGPathElement | null = null
  if (youRing) {
    const randomVariant = scribbleVariants[Math.floor(Math.random() * scribbleVariants.length)]
    youRing.setAttribute('viewBox', randomVariant.viewBox)
    youRing.innerHTML = `<path class="quote__you-ring-path" d="${randomVariant.d}"></path>`
    youRingPath = youRing.querySelector('.quote__you-ring-path') as SVGPathElement | null
  }

  if (youRing && youRingPath) {
    // Keep the hand-drawn feel by slightly varying ring size/angle each load.
    ringFinalScale = Number(gsap.utils.random(0.95, 1.06, 0.01))
    const ringStartScale = Number((ringFinalScale * 0.9).toFixed(2))
    const ringRotation = Number(gsap.utils.random(-7, 7, 1))
    const ringOffsetX = Number(gsap.utils.random(-2, 2, 1))
    const ringOffsetY = Number(gsap.utils.random(-2, 2, 1))

    const pathLength = youRingPath.getTotalLength()
    youRingPath.setAttribute('stroke-dasharray', `${pathLength}`)
    youRingPath.setAttribute('stroke-dashoffset', `${pathLength}`)
    gsap.set(youRing, {
      autoAlpha: 0,
      scale: ringStartScale,
      rotation: ringRotation,
      x: ringOffsetX,
      y: ringOffsetY,
      transformOrigin: '50% 50%',
    })
  }

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: quoteSection,
      start: 'top top',
      end: '+=220%',
      pin: true,
      pinSpacing: true,
      scrub: 1,
      anticipatePin: 1,
      invalidateOnRefresh: true,
    },
  })

  tl.to(
    imageWrap,
    {
      scale: 1.02,
      duration: 0.16,
      ease: 'power1.out',
    },
    0.02
  ).to(
      imageWrap,
      {
        x: () => getDockTransform().x,
        y: () => getDockTransform().y,
        scale: () => getDockTransform().scale,
        duration: 0.56,
        ease: 'power3.inOut',
      },
      0.2
    )
    .to(
      [pulledToken, byToken],
      {
        x: 0,
        duration: 0.28,
        ease: 'back.out(1.8)',
      },
      0.6
    )
    .to(
      quoteHeading,
      {
        y: -8,
        duration: 0.2,
        ease: 'power1.out',
      },
      0.66
    )

  if (youRing && youRingPath) {
    tl.to(
      youRing,
      {
        autoAlpha: 1,
        duration: 0.01,
      },
      0.78
    )
      .to(
        youRingPath,
        {
          strokeDashoffset: 0,
          duration: 0.26,
          ease: 'power2.out',
        },
        0.79
      )
      .to(
        youRing,
        {
          scale: ringFinalScale,
          duration: 0.2,
          ease: 'back.out(2)',
        },
        0.79
      )
  }
}
